// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// Raiz da aplicação
// ============================================================================
//
// Carrega os dados do mapa e mostra-o. Enquanto não estiverem carregados, ou se
// falharem, mostra o estado do serviço — que é a informação útil nesse momento,
// em vez de um ecrã em branco.
//
// PORQUE O ESTADO DE CARGA É EXPLÍCITO E NÃO UM `boolean`
//   São quatro situações distintas com respostas distintas: a carregar, com
//   dados, sem dados por falta de ficheiros (resolve-se correndo o gerador), e
//   sem API (resolve-se arrancando o servidor). Um `boolean` colapsaria as duas
//   últimas, que são exactamente as que precisam de instruções diferentes.
// ============================================================================

import type { DadosMapa } from '@xlv-gva-mpt/dominio';
import { useCallback, useEffect, useState } from 'react';
import { Mapa } from './mapa/Mapa.js';
import {
  CONFIGURACAO_OMISSAO,
  configuracaoDeParametros,
  type ConfiguracaoMapa,
} from './mapa/configuracao.js';

type Situacao =
  | { readonly tipo: 'a_carregar' }
  | { readonly tipo: 'pronto'; readonly dados: DadosMapa }
  | { readonly tipo: 'sem_dados'; readonly detalhe: string }
  | { readonly tipo: 'sem_api'; readonly mensagem: string };

export function App() {
  const [situacao, definirSituacao] = useState<Situacao>({ tipo: 'a_carregar' });
  /**
   * A configuração vem do servidor, não de uma cópia no cliente.
   *
   * ⚠️ Pedida em PARALELO com os dados e nunca a bloqueá-los: a fonte 4.2 diz que
   * «latência de vários segundos com ampulheta não são admissíveis», e o limiar de um
   * aviso não vale atrasar o mapa. Se falhar, fica a omissão e o mapa desenha.
   */
  const [configuracao, definirConfiguracao] = useState<ConfiguracaoMapa>(CONFIGURACAO_OMISSAO);

  /**
   * Conta de recargas. Incrementá-la volta a pedir o mapa ao servidor.
   *
   * ⚠️ **EXISTE POR UM DEFEITO QUE O CLIENTE VIU:** reservava, e o mapa não mudava.
   *   A cache do servidor era invalidada na escrita — mas **o navegador já tinha os
   *   dados**, pedidos uma vez no arranque e nunca mais. O planeador via a reserva
   *   confirmada na janela das opções e a viatura livre na grelha ao lado.
   *
   *   Um mapa que não mostra o que acabou de ser gravado é pior do que um mapa lento:
   *   ensina a não confiar no que está no ecrã.
   */
  const [recargas, definirRecargas] = useState(0);
  const recarregarMapa = useCallback(() => definirRecargas((n) => n + 1), []);

  useEffect(() => {
    const abortar = new AbortController();

    fetch('/api/mapa/dados', { signal: abortar.signal })
      .then(async (r) => {
        if (r.status === 503) {
          // 503 aqui não é avaria: é a API a dizer que os CSV sintéticos não
          // existem. O corpo traz o comando que resolve.
          const corpo = (await r.json()) as { detalhe?: string };
          definirSituacao({ tipo: 'sem_dados', detalhe: corpo.detalhe ?? 'razão não indicada' });
          return;
        }
        if (!r.ok) {
          definirSituacao({ tipo: 'sem_api', mensagem: `resposta HTTP ${r.status}` });
          return;
        }
        definirSituacao({ tipo: 'pronto', dados: (await r.json()) as DadosMapa });
      })
      .catch((erro: unknown) => {
        if (erro instanceof Error && erro.name === 'AbortError') return;
        definirSituacao({
          tipo: 'sem_api',
          mensagem: erro instanceof Error ? erro.message : String(erro),
        });
      });

    fetch('/api/configuracao', { signal: abortar.signal })
      .then(async (r) => {
        if (!r.ok) return;
        const c = (await r.json()) as { parametros_custo?: { chave?: string; valor?: string }[] };
        definirConfiguracao(configuracaoDeParametros(c.parametros_custo ?? []));
      })
      .catch(() => {
        // Silêncio deliberado: sem configuração o mapa usa a omissão. Um erro visível
        // aqui assustava por causa de um limiar de aviso que a maioria dos dias não
        // dispara — e o mapa em si está perfeitamente funcional.
      });

    return () => abortar.abort();
    // ⚠️ `recargas` nas dependências: é o que faz o mapa voltar a ser pedido depois de
    // uma escrita. Sem isto o efeito corria uma vez e o ecrã ficava congelado no passado.
  }, [recargas]);

  if (situacao.tipo === 'pronto') {
    return <Mapa dados={situacao.dados} configuracao={configuracao} recarregar={recarregarMapa} />;
  }

  return (
    <main className="pagina">
      <header>
        <p className="marca">( ·:·●●·:· ) xlVISION | Powering Business Together!</p>
        <h1>xlv-GVA-MPT</h1>
        <p className="subtitulo">Mapa de Planeamento de Turismo · Grupo Vale do Ave</p>
      </header>

      <section className="cartao">
        <h2>Mapa</h2>
        {situacao.tipo === 'a_carregar' && <p>a carregar o ano…</p>}

        {situacao.tipo === 'sem_dados' && (
          <>
            <p className="erro">Sem dados para mostrar.</p>
            <p className="nota">{situacao.detalhe}</p>
            <p className="nota">
              Os dados sintéticos são reproduzíveis a partir da semente fixa do gerador e por
              isso não estão versionados. Nunca contêm dados pessoais reais.
            </p>
          </>
        )}

        {situacao.tipo === 'sem_api' && (
          <>
            <p className="erro">API inacessível: {situacao.mensagem}</p>
            <p className="nota">
              Arrancar com <code>npm run dev -w @xlv-gva-mpt/api</code>. O estado das
              dependências está em <code>/api/health?detalhe</code>.
            </p>
          </>
        )}
      </section>
    </main>
  );
}
